/**
 * advance-odp.ts — Orbital Defence Platform per-tick pass
 * (v3M2.2b deployment + level scaling, v3M2.2c rebuild queue).
 *
 * For each Structure of kind `orbital-defence-platform`, this pass:
 *
 *   1. **Deploys the ring on first activation.** When the ODP reaches
 *      level 1 and `ringPlatformShipIds` is `undefined`, it spawns
 *      `ODP_RING_COUNT` (6) ring-platform `ShipInstance`s on a fixed
 *      orbit radius around the host planet, equi-spaced. All six come
 *      up in a single tick — initial deployment skips the rebuild
 *      queue per STRUCTURES §3.4 (the ring is part of the activation,
 *      not a series of rebuilds). Each platform carries
 *      `chassisLevel = structure.level` and is auto-armed via the
 *      existing `initWeaponSlots` pipeline.
 *
 *   2. **Syncs chassis level on upgrade.** When `structure.level`
 *      advances past any platform's recorded `chassisLevel`, all live
 *      platforms get their `chassisLevel` updated to match and their
 *      `currentHull` scaled proportionally upward so the level-up
 *      bonus shows immediately.
 *
 *   3. **Scrubs destroyed slots and enqueues rebuilds (v3M2.2c).**
 *      If a slot's referenced ship is no longer in `state.ships`, the
 *      slot is nulled and a rebuild entry is queued — only ONE entry
 *      per slot, so a single combat death produces a single queued
 *      rebuild. Per-platform Credits + Minerals are deducted at
 *      enqueue time. If the empire can't afford it, the slot stays
 *      empty and the next pass retries.
 *
 *   4. **Ticks the rebuild queue head.** Only the head entry ticks
 *      each pass — STRUCTURES §3.4 "Only one lost platform rebuilds at
 *      a time". On completion the new platform spawns at the slot's
 *      angle and the entry is popped.
 *
 * Runs after `advanceCombat` (so destruction is observable) and after
 * `advanceStructures` (so level promotions are observable). Pure
 * mutation, deterministic — iterates structures and slots in id order.
 */

import { allocEntityId } from '../state/types/index';
import { initWeaponSlots } from './scenario';
import { effectiveMaxHull, effectiveMaxShields } from './research-effects';
import {
  ODP_PLATFORM_CHASSIS,
  odpOrbitRadius,
  odpRebuildCost,
  odpRebuildTicks,
  odpRingCount,
} from './balance-space-structures';
import type { Empire, GameState, ShipInstance, Structure } from '../state/types/index';

export function advanceOdp(state: GameState): void {
  // Iterate structures in id order for determinism.
  const structureIds = [...state.structures.keys()].sort((a, b) => a - b);
  for (const sid of structureIds) {
    const structure = state.structures.get(sid);
    if (!structure) continue;
    if (structure.kind !== 'orbital-defence-platform') continue;
    if (structure.level < 1) continue;

    const planet = state.planets.get(structure.planetId);
    if (!planet) continue;
    if (planet.ownerEmpireId === null) continue; // can't deploy a ring on an unowned planet
    const empire = state.empires.get(planet.ownerEmpireId);
    if (!empire) continue;

    // First-activation deployment: spawn all 6 platforms instantly.
    // Distinguished from rebuild scenarios by the `undefined` state of
    // `ringPlatformShipIds`. After this branch the field is always
    // defined, so subsequent destruction-and-rebuild flows go through
    // the queue.
    const sd = state.structuresData;
    const ringCount = odpRingCount(sd);
    if (!structure.ringPlatformShipIds) {
      structure.ringPlatformShipIds = new Array(ringCount).fill(null);
      for (let slot = 0; slot < ringCount; slot++) {
        const ship = spawnRingPlatform(
          state,
          structure,
          planet.ownerEmpireId,
          slot,
        );
        if (ship !== null) structure.ringPlatformShipIds[slot] = ship.id;
      }
    } else {
      // Subsequent passes: scrub destroyed slots and enqueue rebuilds.
      scrubAndEnqueueRebuilds(state, structure, empire);
      // Tick the queue head (one rebuild at a time per STRUCTURES §3.4).
      tickRebuildQueue(state, structure, empire);
    }

    // Sync chassisLevel on each live platform. If structure.level
    // exceeds any platform's chassisLevel, scale the platform up.
    for (const refId of structure.ringPlatformShipIds) {
      if (refId === null) continue;
      const ship = state.ships.get(refId);
      if (!ship) continue;
      const currentLevel = ship.chassisLevel ?? 1;
      if (structure.level > currentLevel) {
        scalePlatformLevel(state, ship, currentLevel, structure.level);
      }
    }
  }
}

/**
 * Walk the slot array; for any slot whose ship is missing from
 * `state.ships` (despawned by combat / culling), null the slot and
 * enqueue a rebuild entry — but only if the slot isn't already in
 * the queue (defensive against double-enqueue on consecutive passes).
 *
 * Cost is deducted at enqueue time. Insufficient resources leave the
 * slot empty and skip enqueue; the next pass retries the deduction.
 */
function scrubAndEnqueueRebuilds(
  state: GameState,
  structure: Structure,
  empire: Empire,
): void {
  if (!structure.ringPlatformShipIds) return;
  const sd = state.structuresData;
  const cost = odpRebuildCost(sd);
  const ticks = odpRebuildTicks(sd);
  // Build a set of slot indices already queued for rebuild so we don't
  // double-enqueue on the second tick after a death.
  const queued = new Set<number>();
  if (structure.rebuildQueue) {
    for (const entry of structure.rebuildQueue) queued.add(entry.slotIndex);
  }
  for (let slot = 0; slot < structure.ringPlatformShipIds.length; slot++) {
    const refId = structure.ringPlatformShipIds[slot];
    if (refId === null) continue;
    if (state.ships.has(refId)) continue;
    // Slot's ship is gone — null the slot and try to enqueue a rebuild.
    structure.ringPlatformShipIds[slot] = null;
    if (queued.has(slot)) continue;
    if (
      empire.resources.credits < cost.credits ||
      empire.resources.minerals < cost.minerals
    ) {
      // Can't afford this tick. Don't enqueue; next pass retries.
      continue;
    }
    empire.resources.credits -= cost.credits;
    empire.resources.minerals -= cost.minerals;
    if (!structure.rebuildQueue) structure.rebuildQueue = [];
    structure.rebuildQueue.push({
      slotIndex: slot,
      rebuildTicksRemaining: ticks,
    });
    queued.add(slot);
  }
  // Clean up: if the queue exists but is empty, drop the array so the
  // save-format footprint stays minimal on stable ODPs.
  if (structure.rebuildQueue && structure.rebuildQueue.length === 0) {
    structure.rebuildQueue = undefined;
  }
}

/**
 * Tick the head entry of `structure.rebuildQueue`. Only ONE entry
 * advances per pass per ODP — STRUCTURES §3.4 "Only one lost platform
 * rebuilds at a time; simultaneous losses queue."
 *
 * On completion (rebuildTicksRemaining hits 0), the new platform
 * spawns at the slot's fixed angle, the entry is popped, and the
 * next entry (if any) becomes head and starts ticking on the
 * following pass.
 */
function tickRebuildQueue(
  state: GameState,
  structure: Structure,
  empire: Empire,
): void {
  if (!structure.rebuildQueue || structure.rebuildQueue.length === 0) return;
  const head = structure.rebuildQueue[0]!;
  head.rebuildTicksRemaining -= 1;
  if (head.rebuildTicksRemaining > 0) return;

  // Rebuild complete — spawn the platform and pop the queue.
  const ship = spawnRingPlatform(
    state,
    structure,
    empire.id,
    head.slotIndex,
  );
  if (ship !== null && structure.ringPlatformShipIds) {
    structure.ringPlatformShipIds[head.slotIndex] = ship.id;
  }
  structure.rebuildQueue.shift();
  if (structure.rebuildQueue.length === 0) {
    structure.rebuildQueue = undefined;
  }
}

/**
 * Spawn a single ring-platform ShipInstance at the slot's angle around
 * the host planet. `chassisLevel` is set to the parent ODP's current
 * level so combat / scanner code applies the per-level multiplier.
 *
 * Returns the new ship, or null if the synthetic 'ODP Platform'
 * template is missing for this empire (defensive — should never
 * happen because `loader.ts` injects it for every race).
 */
function spawnRingPlatform(
  state: GameState,
  structure: Structure,
  empireId: number,
  slotIndex: number,
): ShipInstance | null {
  const empire = state.empires.get(empireId);
  if (!empire) return null;
  const tmpl = state.shipTemplates.get(`${empire.raceId}:${ODP_PLATFORM_CHASSIS}`);
  if (!tmpl) return null;
  const planet = state.planets.get(structure.planetId);
  if (!planet) return null;

  const sd = state.structuresData;
  const angle = (slotIndex / odpRingCount(sd)) * Math.PI * 2;
  const orbit = odpOrbitRadius(sd);
  const sx = planet.systemX + Math.cos(angle) * orbit;
  const sy = planet.systemY + Math.sin(angle) * orbit;

  const chassisLevel = Math.max(1, structure.level);
  // currentHull seeded at the level-scaled max so the platform starts
  // at full hull for its current upgrade tier.
  const ship: ShipInstance = {
    id: allocEntityId(state),
    chassis: ODP_PLATFORM_CHASSIS,
    ownerEmpireId: empireId,
    currentHull: effectiveMaxHull(state, empire, tmpl, { chassisLevel }),
    currentShields: effectiveMaxShields(state, empire, tmpl),
    currentEnergy: 0,
    disabledTicks: 0,
    cloaked: false,
    systemId: planet.systemId,
    sx,
    sy,
    moveOrder: null,
    jumpOriginSystemId: null,
    jumpTargetSystemId: null,
    jumpArrivalAngle: null,
    jumpTargetGateId: null,
    pendingWarpGateLinkId: null,
    jumpTicksRemaining: 0,
    chargeUpTicksRemaining: 0,
    jumpFuelCostPaid: 0,
    jumpDurationTotal: 0,
    lastFiredTick: null,
    lastFiredTargetShipId: null,
    pendingJumpSystemId: null,
    pendingJumpArrivalAngle: null,
    pendingColoniseTargetId: null,
    constructionOrder: null,
    bombardOrder: null,
    weaponSlots: initWeaponSlots(
      tmpl.weaponLoadout,
      state.weapons,
      empire.researchedTechs,
    ),
    chassisLevel,
    // v3M22.1 — ship-commands defaults; ring platforms are 'idle' and
    // never participate in player commands (they're stationary).
    commandKind: 'idle',
    waypoints: [],
    guardTargetKind: null,
    guardTargetId: null,
  };
  state.ships.set(ship.id, ship);
  empire.shipIds.push(ship.id);
  return ship;
}

/**
 * Scale a live ring platform from `oldLevel` to `newLevel`. Hull is
 * scaled by the ratio of new max-hull to old max-hull so the
 * level-up bonus is immediately visible (a damaged L1 platform that
 * upgrades to L2 keeps its current damage ratio, not its absolute
 * damage). Shield max is unchanged for v1 (shields don't scale by
 * chassisLevel yet — flagged for v3M10 balance pass).
 */
function scalePlatformLevel(
  state: GameState,
  ship: ShipInstance,
  oldLevel: number,
  newLevel: number,
): void {
  const empire = state.empires.get(ship.ownerEmpireId);
  if (!empire) return;
  const tmpl = state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
  if (!tmpl) return;
  const oldMax = effectiveMaxHull(state, empire, tmpl, { chassisLevel: oldLevel });
  const newMax = effectiveMaxHull(state, empire, tmpl, { chassisLevel: newLevel });
  if (oldMax > 0 && newMax > 0) {
    ship.currentHull = (ship.currentHull / oldMax) * newMax;
  }
  ship.chassisLevel = newLevel;
}
